import { computed, ref } from '@vue/reactivity';
import { watch } from '@vue-reactivity/watch';
import { getRecordConsolePlugin, record } from 'rrweb';

import { getRecordNetworkPlugin } from './network-plugin';
import Utils from '../utils';

/**
 * The possible states of the recorder.
 *
 * @typedef {'recording' | 'stopped' | 'paused'} States
 */

/**
 * The possible states of the recorder.
 *
 * @typedef {import('@vue/reactivity').Ref} Ref<T>
 * @template {*} T
 */

/**
 * An array of recorded events with their timestamps.
 *
 * @type {Ref<import('@rrweb/types').eventWithTime[]>}
 */
const events = ref([]);

/**
 * @type {Ref<States>}
 */
const state = ref('stopped');
const isShowingUI = ref(false);

/**
 * @type {(() => void) | undefined}
 */
let stopFunction;

watch(state, () => {
	// eslint-disable-next-line no-console
	console.info('state changed to', state.value);
});

export const useRecorder = () => {
	const start = () => {
		state.value = 'recording';
		stopFunction = record({
			emit(event) {
				if (state.value === 'recording') {
					events.value.push(event);
				}
			},
			plugins: [
				getRecordConsolePlugin({
					level: ['info', 'log', 'warn', 'error'],
					lengthThreshold: Number.MAX_SAFE_INTEGER,
				}),
				getRecordNetworkPlugin({
					recordHeaders: true,
					recordBody: true,
					recordInitialRequests: false,
				}),
			],
		});
	};

	const stop = () => {
		state.value = 'stopped';
		if (stopFunction) {
			stopFunction();
			stopFunction = undefined;
		}
	};

	const pause = () => {
		state.value = 'paused';
	};

	const clear = () => {
		events.value = [];
	};

	const resume = () => {
		state.value = 'recording';
	};

	const save = () => {
		const body = JSON.stringify(events.value);
		const myFile = new File([body], 'record.kreplay');
		Utils.download(myFile, 'application/json', 'record.kreplay');
	};

	const isRecording = computed(() => state.value === 'recording');

	/**
	 * @param {boolean} value
	 */
	const showUI = value => {
		isShowingUI.value = value;
	};

	return {
		start,
		save,
		stop,
		pause,
		resume,
		clear,
		isRecording,
		showUI,
		isShowingUI,
		state,
		events,
	};
};
